<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<template>
  <cytomine-modal :active="active" :title="$t('add-images')" @close="activeInternal = false">
    <b-loading :is-full-page="false" :active="loading" class="small" />
    <template v-if="!loading">
      <template>
        <!-- <b-input class="search-images" v-model="searchString" :placeholder="$t('search-placeholder')"
      type="search" icon="search" /> -->

        <!-- <cytomine-table
        :collection="imageCollection"
        :currentPage.sync="currentPage"
        :perPage.sync="perPage"
        :sort.sync="sortField"
        :order.sync="sortOrder"
        :detailed="false"
      >
        <template #default="{row: image}">
          <b-table-column :label="$t('overview')">
            <image-thumbnail :image="image" :size="128" :key="`${image.id}-thumb-128`" :extra-parameters="{Authorization: 'Bearer ' + shortTermToken }"/>
          </b-table-column>

          <b-table-column field="originalFilename" :label="$t('name')" sortable>
            {{ image.originalFilename }}
          </b-table-column>

          <b-table-column field="created" :label="$t('created-on')" sortable>
            {{ Number(image.created) | moment('ll LT') }}
          </b-table-column>

          <b-table-column label=" " centered>
            <button v-if="wasAdded(image)" class="button is-small is-link" disabled>
              {{$t('button-added')}}
            </button>
            <span v-else-if="isInProject(image)">
              {{$t('already-in-project')}}
            </span>
            <button v-else class="button is-small is-link" @click="addImage(image)">
              {{$t('button-add')}}
            </button>
          </b-table-column>
        </template>

<template #empty>
          <div class="content has-text-grey has-text-centered">
            <p>{{$t('no-image')}}</p>
          </div>
        </template>
</cytomine-table> -->

        <b-table v-if="pendingFiles.length === 0" :data="pendingFiles">
          <template #empty>
            <div class="content has-text-grey has-text-centered">
              <p>{{ $t('no-image') }}</p>
            </div>
          </template>
        </b-table>


        <zk-table v-else :data="pendingFiles" :columns="[
          {
            label: $t('name'),
            prop: 'name',
          },
          {
            label: $t('status'),
            prop: 'uploaded',
            type: 'template',
            template: 'status',
          },
          {
            label: $t('start-upload'),
            prop: 'filepath',
            type: 'template',
            template: 'actions',
          },
        ]" :is-fold="false" :expand-type="false" :selection-type="false">
          <template slot="status" slot-scope="scope">
            <span v-if="scope.row.uploaded" class="tag" :class="scope.row.uploaded ? 'is-success' : ''">
              {{ $t('uploaded') }}
            </span>
          </template>
          <template slot="actions" slot-scope="scope">
            <div v-if="scope.row.filepath !== ''" class="buttons">
              <button class="button is-small is-link" @click="addImage(scope.row.filepath)">
                {{ $t('button-add') }}
              </button>
            </div>
          </template>
        </zk-table>
      </template>
    </template>
  </cytomine-modal>
</template>

<script>
import { get } from '@/utils/store-helpers';
import { AbstractImageCollection } from 'cytomine-client';
import ZkTable from 'vue-table-with-tree-grid'
import CytomineModal from '@/components/utils/CytomineModal';
import CytomineTable from '@/components/utils/CytomineTable';
import ImageThumbnail from '@/components/image/ImageThumbnail';

import ViewerClient from '@/utils/viewer-client';

export default {
  name: 'add-image-modal',
  props: {
    active: Boolean,
  },
  components: {
    ImageThumbnail,
    CytomineTable,
    CytomineModal,
    ZkTable,
  },
  data() {
    return {
      activeInternal: false,
      pendingFiles: [],
      loading: true,
      perPage: 10,
      searchString: '',
      idsAddedImages: [],
      currentPage: 1,
      sortField: 'created',
      sortOrder: 'desc',
    };
  },
  computed: {
    project: get('currentProject/project'),
    shortTermToken: get('currentUser/shortTermToken'),
    imageCollection() {
      let collection = new AbstractImageCollection({
        project: this.project.id,
      });
      if (this.searchString) {
        collection['originalFilename'] = {
          ilike: encodeURIComponent(this.searchString)
        };
      }

      return collection;
    },
  },
  watch: {
    activeInternal(val) {
      this.$emit('update:active', this.activeInternal);
      if (val) {
        this.idsAddedImages = [];
        this.refetch();
      }
    },
    active: {
      handler: function (newVal) {
        this.activeInternal = newVal;
      },
      immediate: true,
    },
  },
  methods: {
    async addImage(filepath) {
      let propsTranslation = { imageName: filepath, projectName: this.project.name };
      try {
        this.activeInternal = false;
        await ViewerClient.importImage(filepath);
        this.$notify({
          type: 'success',
          text: this.$t('notif-success-add-image', propsTranslation)
        });

        let updatedProject = this.project.clone();
        updatedProject.numberOfImages++;
        this.$store.dispatch('currentProject/updateProject', updatedProject);

        await this.refetch();
      }
      catch (error) {
        console.log(error);
        this.$notify({
          type: 'error',
          text: this.$t('notif-error-add-image', propsTranslation)
        });
      }
    },
    isInProject(image) {
      return image.inProject;
    },
    wasAdded(image) {
      return this.idsAddedImages.includes(image.id);
    },
    async refetch() {
      const sep = "/";

      let _files = [];
      let _root = {
        children: _files,
      }
      let _addFolder = (folderPath, root = _root, dir = '') => {
        if (folderPath === '') return root;

        let subdirs = folderPath.split(sep);
        let name = subdirs[0];
        let others = subdirs.slice(1);
        let filepath = dir === '' ? name : dir + sep + name;

        let data = {
          name,
          dir,
          filepath: '',
          uploaded: false,
          children: [],
        };

        if (!(name in root.children)) {
          root.children.push(data);
        }

        return _addFolder(others.join(sep), data, filepath);
      }
      let _addFilesRecursive = (files, root) => {
        files.forEach(file => {
          let fileSubDir = file.dir;
          if (fileSubDir.startsWith(root)) {
            fileSubDir = fileSubDir.slice(root.length);
          }

          if (fileSubDir === '') {
            _files.push(file);
          } else {
            let sub = _addFolder(file.dir);
            sub.children.push(file);
          }
        })
      }
      _addFilesRecursive(await ViewerClient.getPendingFiles());
      this.pendingFiles = _files;
    },
  },
  async created() {
    await this.refetch();
    this.loading = false;
  }
};
</script>

<style>
.zk-table {
  border-color: #222;
  border-radius: 8px;
}

.zk-table__header-row {
  border-bottom: 1px solid #222 !important;
}

.zk-table__body-row {
  border-top: 1px solid #222 !important;
}

.zk-table__header-cell,
.zk-table__body-cell {
  background-color: #333;
  color: whitesmoke;
}
</style>

<style scoped>
>>>.animation-content {
  max-width: 60% !important;
  width: 60%;
}

>>>.modal-card {
  width: 100%;
  height: 80vh;
}

>>>.image-thumbnail {
  max-height: 4rem;
  max-width: 10rem;
}
</style>
